import type { VendorDashboardOrder } from '@cravo/core'

export const URGENT_THRESHOLD_MS = 4 * 60_000
export const DEFAULT_ACCEPT_TIMEOUT_MIN = 5

export function getOrderError(error: unknown, fallback = 'Something went wrong.'): string {
  if (typeof error === 'object' && error && 'message' in error && typeof error.message === 'string') {
    return error.message
  }
  return fallback
}

export function getTimeAgo(value: string): string {
  const createdAt = new Date(value)
  if (Number.isNaN(createdAt.getTime())) return ''
  const diffMinutes = Math.max(0, Math.floor((Date.now() - createdAt.getTime()) / 60000))
  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes} min ago`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
}

export function titleCase(value: string): string {
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

// ── Order status label ───────────────────────────────────────────────
// Single source of truth for the human-readable status text that
// shows on cards, in the detail header, in toasts, and in the past-
// orders table. Backend transitions are identical for restaurants
// and stores — only the consumer-facing word "Preparing" swaps to
// "Packing" when the order belongs to a STORE vendor (industry
// standard: DoorDash convenience, Uber Direct, Glovo store category).
//
// `isStore` is sourced from `order.businessEntityType === 'store'` at
// each call site so the swap is per-order and survives the
// multi-vendor tenant case. `t` is the i18n translator from
// `useTranslation()`; pass it in rather than re-importing here so the
// helper stays pure and tree-shakable.
//
// Any status not in the map falls back to `titleCase(status)` so
// future backend statuses don't crash the UI — they just render
// untranslated until the i18n key lands.
export function formatOrderStatusLabel(
  status: string,
  isStore: boolean,
  t: (key: string) => string,
): string {
  const normalized = status.toLowerCase()
  const keyMap: Record<string, string> = {
    pending: 'orders.status.pending',
    confirmed: 'orders.status.confirmed',
    preparing: isStore ? 'orders.status.packing' : 'orders.status.preparing',
    ready: 'orders.status.ready',
    on_the_way: 'orders.status.onTheWay',
    delivered: 'orders.status.delivered',
    cancelled: 'orders.status.cancelled',
  }
  const key = keyMap[normalized]
  return key ? t(key) : titleCase(status)
}

export function getOrderTypeLabel(order: VendorDashboardOrder | { orderType: string; type?: string | null }): string {
  return titleCase(order.orderType || ('type' in order ? order.type ?? 'order' : 'order'))
}

export function statusBadgeClass(status: string): string {
  switch (status) {
    case 'pending':
      return 'warning'
    case 'confirmed':
    case 'ready':
    case 'on_the_way':
      return 'info'
    case 'preparing':
      return 'warning'
    case 'delivered':
      return 'success'
    case 'cancelled':
      return 'danger'
    default:
      return ''
  }
}

export function getRequestExpiry(
  order: { createdAt: string; expiresAt?: string | null },
  vendorAcceptTimeoutMin: number,
): Date | null {
  if (order.expiresAt) {
    const value = new Date(order.expiresAt)
    if (!Number.isNaN(value.getTime())) return value
  }
  const createdAt = new Date(order.createdAt)
  if (Number.isNaN(createdAt.getTime())) return null
  return new Date(createdAt.getTime() + vendorAcceptTimeoutMin * 60_000)
}

// Reads tenant-aware status colors from CSS variables at call time so the
// countdown timer follows the active brand theme. Falls back to universal
// status hexes on SSR or pre-theme-load.
function readVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

export function getCountdownColor(fraction: number): string {
  if (fraction > 0.5) return readVar('--brand-success', '#16a34a')
  if (fraction > 0.2) return readVar('--brand-warning', '#f59e0b')
  return readVar('--brand-error', '#dc2626')
}

/**
 * Format payment-method enum from backend (e.g. "COD", "CARD", "WALLET").
 * Keeps acronyms uppercase rather than mangling to "Cod".
 */
export function formatPaymentMethod(value: string): string {
  if (!value) return '-'
  return value
    .replace(/_/g, ' ')
    .split(' ')
    .map((part) => (part.length <= 3 ? part.toUpperCase() : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()))
    .join(' ')
}

/**
 * Map raw delivery-provider id (snake_case backend value) to a
 * human-readable label for vendor-facing copy. Unknown providers fall
 * back to a title-cased version so future providers shipping before
 * this map is updated still render something readable.
 */
export function prettyProviderName(provider: string): string {
  const known: Record<string, string> = {
    uber_direct: 'Uber Direct',
    postmates: 'Postmates',
    lalamove: 'Lalamove',
    doordash_drive: 'DoorDash Drive',
    stuart: 'Stuart',
    wolt_drive: 'Wolt Drive',
    gogox: 'GoGoX',
    gosend: 'GoSend',
  }
  return (
    known[provider] ??
    provider
      .split('_')
      .map((p) => (p.length === 0 ? p : p[0]!.toUpperCase() + p.slice(1)))
      .join(' ')
  )
}
